import { useEffect, useState } from "react";

/**
 * Returns true while the user is holding the Shift key.
 * Listens at window level; clears on keyup, blur, and visibility change to
 * avoid "stuck Shift" after tab-switch.
 *
 * Used by the dev-mode permission reveal — see `vault/how-to/dev-mode-permission-hover.md`.
 */
export function useShiftKey(): boolean {
    const [held, setHeld] = useState(false);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => { if (e.key === "Shift") setHeld(true); };
        const onKeyUp = (e: KeyboardEvent) => { if (e.key === "Shift") setHeld(false); };
        const onBlur = () => setHeld(false);
        const onVisibility = () => { if (document.hidden) setHeld(false); };

        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        window.addEventListener("blur", onBlur);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
            window.removeEventListener("blur", onBlur);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    return held;
}

/** True in local dev (CRA dev build or localhost). */
export function isDevMode(): boolean {
    if (typeof window === "undefined") return false;
    return (
        process.env.NODE_ENV === "development" ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"
    );
}
